import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import ListingCard from '../components/ListingCard';
import AddListingModal from '../components/AddListingModal';
import EditListingModal from '../components/EditListingModal';
import Pagination from '../components/Pagination';

const LISTINGS_PER_PAGE = 12;

function Nehnutelnosti({ isAdmin, onLogout }) {
  const [allListings, setAllListings] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(['prenájom', 'predaj']);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [listingToEdit, setListingToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const { pageNumber } = useParams();
  const navigate = useNavigate();
  const currentPage = pageNumber ? parseInt(pageNumber, 10) : 1;

  // Universal scroll position restoration
  useEffect(() => {
    const isReturningFromListing = sessionStorage.getItem('lastListingPage') === '/nehnutelnosti' || 
                                   sessionStorage.getItem('lastListingPage')?.startsWith('/nehnutelnosti/');
    
    if (isReturningFromListing) {
      console.log('🔄 Detected return to nehnutelnosti page from listing detail');
      
      // Clear the return flag
      sessionStorage.removeItem('lastListingPage');
      
      // Restore scroll position after content loads
      const scrollTimeout = setTimeout(() => {
        const savedScrollPosition = sessionStorage.getItem('scrollPosition-/nehnutelnosti') || 
                                   sessionStorage.getItem(`scrollPosition-/nehnutelnosti/${currentPage}`);
        
        if (savedScrollPosition) {
          const scrollY = parseInt(savedScrollPosition, 10);
          console.log(`📍 Restoring scroll position to: ${scrollY}px`);
          
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            window.scrollTo({
              top: scrollY,
              behavior: 'auto'
            });
          });
          
          // Clean up the scroll position after restoring
          sessionStorage.removeItem('scrollPosition-/nehnutelnosti');
          sessionStorage.removeItem(`scrollPosition-/nehnutelnosti/${currentPage}`);
        }
      }, 100);
      
      return () => clearTimeout(scrollTimeout);
    } else {
      // Normal page navigation - scroll to top
      window.scrollTo(0, 0);
    }
  }, [currentPage, allListings.length]);

  // Scroll to top when page changes (but not when returning from listing)
  useEffect(() => {
    const isReturningFromListing = sessionStorage.getItem('lastListingPage') === '/nehnutelnosti' || 
                                   sessionStorage.getItem('lastListingPage')?.startsWith('/nehnutelnosti/');
    
    if (!isReturningFromListing) {
      window.scrollTo(0, 0);
    }
  }, [currentPage]);

  useEffect(() => {
    // Fetch all real estate listings
    const fetchNehnutelnosti = async () => {
      try {
        setIsLoading(true);
        const res = await fetch('https://eurocentrumv1.onrender.com/listings');
        const data = await res.json();
        
        // Filter to only include listings with "byt" or "dom" tags and exclude "neplatiči"
        const filteredData = data.filter(listing => {
          // Check if listing has tags
          if (!listing.tags || !Array.isArray(listing.tags)) {
            return false;
          }
          
          // Include only if it has real estate tag
          const hasRealEstateTags = listing.tags.includes('byt') || 
                                    listing.tags.includes('dom') || 
                                    listing.tags.includes('pozemok') || 
                                    listing.tags.includes('nebytový priestor') || 
                                    listing.tags.includes('priemyselná budova');
          if (!hasRealEstateTags) {
            return false;
          }
          
          // Exclude neplatiči
          if (listing.tags.includes('neplatiči')) {
            return false;
          }
          
          return true;
        });
        
        // Store all listings
        setAllListings(filteredData);
      } catch (error) {
        console.error('Error fetching real estate listings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNehnutelnosti();
  }, []);

  const refreshAll = async () => {
    try {
      setIsLoading(true);
      
      const res = await fetch('https://eurocentrumv1.onrender.com/listings');
      const data = await res.json();
      
      // Filter to only include listings with real estate tags and exclude "neplatiči"
      const filteredData = data.filter(listing => {
        // Check if listing has tags
        if (!listing.tags || !Array.isArray(listing.tags)) {
          return false;
        }
        
        // Include only if it has real estate tag
        const hasRealEstateTags = listing.tags.includes('byt') || 
                                  listing.tags.includes('dom') || 
                                  listing.tags.includes('pozemok') || 
                                  listing.tags.includes('nebytový priestor') || 
                                  listing.tags.includes('priemyselná budova');
        if (!hasRealEstateTags) {
          return false;
        }
        
        // Exclude neplatiči
        if (listing.tags.includes('neplatiči')) {
          return false;
        }
        
        return true;
      });
      
      setAllListings(filteredData);
    } catch (error) {
      console.error('Error refreshing listings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterClick = (filter) => {
    setSelectedFilters(prev => {
      if (prev.includes(filter)) {
        return prev.filter(f => f !== filter);
      } else {
        return [...prev, filter];
      }
    });
  };

  const handleTagClick = (tag) => {
    // Navigate to the appropriate page based on the tag
    if (tag === 'auto') {
      window.location.href = '/auta';
    } else if (tag === 'byt' || tag === 'dom' || tag === 'pozemok' || tag === 'nebytový priestor' || tag === 'priemyselná budova') {
      window.location.href = '/nehnutelnosti';
    }
  };

  const handleAddListing = (newListing) => {
    console.log("Adding new listing to Nehnutelnosti:", newListing);
    
    // Instead of forcing to top, just refresh all listings from server
    // which will respect the server-side sorting
    refreshAll();
    
    setShowAddModal(false);
  };

  const handleEditClick = (listing) => {
    setListingToEdit(listing);
    setShowEditModal(true);
  };

  // Enhanced edit modal close handler that preserves scroll position
  const handleEditModalClose = () => {
    console.log('🔄 Admin finished editing listing on Nehnutelnosti - preserving scroll position');
    
    // Save current scroll position before closing modal
    const currentPath = window.location.pathname;
    const scrollY = window.scrollY || window.pageYOffset;
    sessionStorage.setItem(`scrollPosition-${currentPath}`, scrollY.toString());
    
    // Set flag to indicate we should restore position after refresh
    sessionStorage.setItem('preserveScrollAfterEdit', 'true');
    
    console.log(`💾 Saved scroll position for admin edit: ${scrollY}px`);
    
    setShowEditModal(false);
    setListingToEdit(null);
  };

  // Enhanced refresh function that restores scroll position after admin edits
  const handleRefreshAfterEdit = async () => {
    await refreshAll();
    
    // Check if we need to restore scroll position after edit
    const shouldPreserveScroll = sessionStorage.getItem('preserveScrollAfterEdit');
    
    if (shouldPreserveScroll) {
      console.log('🔄 Restoring scroll position after admin edit on Nehnutelnosti');
      
      // Remove the flag
      sessionStorage.removeItem('preserveScrollAfterEdit');
      
      // Restore scroll position after content loads
      setTimeout(() => {
        const currentPath = window.location.pathname;
        const savedScrollPosition = sessionStorage.getItem(`scrollPosition-${currentPath}`);
        
        if (savedScrollPosition) {
          const scrollY = parseInt(savedScrollPosition, 10);
          console.log(`📍 Restoring scroll position after edit: ${scrollY}px`);
          
          window.scrollTo({
            top: scrollY,
            behavior: 'auto'
          });
          
          // Clean up
          sessionStorage.removeItem(`scrollPosition-${currentPath}`);
        }
      }, 100);
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Apply the selected filters (predaj/prenájom) to the listings
  const filteredListings = allListings
    .filter(listing => {
      // If no tags, don't display
      if (!listing.tags) return false;
      
      // If no filters selected or both filters selected, show all listings
      if (selectedFilters.length === 0 || 
          (selectedFilters.includes('predaj') && selectedFilters.includes('prenájom'))) {
        return true;
      }
      
      // Otherwise, filter by the selected tags
      return listing.tags.some((tag) => selectedFilters.includes(tag));
    });

  // Calculate pagination
  const totalPages = Math.ceil(filteredListings.length / LISTINGS_PER_PAGE);
  const startIndex = (currentPage - 1) * LISTINGS_PER_PAGE;
  const endIndex = startIndex + LISTINGS_PER_PAGE;
  const displayedListings = filteredListings.slice(startIndex, endIndex);

  // Redirect to page 1 if current page is invalid
  useEffect(() => {
    if (filteredListings.length > 0 && totalPages > 0 && currentPage > totalPages) {
      navigate('/nehnutelnosti');
    }
  }, [currentPage, totalPages, filteredListings.length, navigate]);

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {/* Header with Add Button for Admin */}
      <div style={styles.header}>
        <h2 style={styles.heading}>NEHNUTEĽNOSTI (AJ NA SPLÁTKY)</h2>
        {isAdmin && (
          <button
            style={styles.addButton}
            onClick={() => setShowAddModal(true)}
          >
            + Pridať nový inzerát
          </button>
        )}
      </div>

      {/* Filter Buttons */}
      <div style={styles.buttonRow}>
        {['prenájom', 'predaj'].map((f) => (
          <button
            key={f}
            style={selectedFilters.includes(f) ? styles.activeButton : styles.filterButton}
            onClick={() => handleFilterClick(f)}
          >
            {f === 'prenájom' ? 'NA NÁJOM' : f === 'predaj' ? 'NA PREDAJ' : f.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={styles.loadingText}>Načítavam nehnuteľnosti...</p>
        </div>
      ) : (
        <div style={styles.listingsContainer}>
          <AnimatePresence>
            {displayedListings.length > 0 ? (
              displayedListings.map((listing) => (
              <motion.div
                key={listing._id}
                layout
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, scale: 0.9 }}
                variants={itemVariants}
                transition={{ duration: 0.3 }}
              >
                <ListingCard
                  listing={listing}
                  isAdmin={isAdmin}
                  onRefresh={refreshAll}
                  onTagClick={handleTagClick}
                  onEditClick={handleEditClick}
                />
              </motion.div>
              ))
            ) : (
              <div style={styles.noListings}>
                <h3>Žiadne nehnuteľnosti na zobrazenie</h3>
                <p>Momentálne nemáme žiadne nehnuteľnosti s vybranými filtrami.</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Pagination */}
      <Pagination 
        currentPage={currentPage}
        totalPages={totalPages}
        basePath="/nehnutelnosti"
      />

      {showAddModal && isAdmin && (
        <AddListingModal
          listingToEdit={null}
          onClose={() => setShowAddModal(false)}
          onRefresh={refreshAll}
          onAddListing={handleAddListing}
        />
      )}

      {showEditModal && isAdmin && (
        <EditListingModal
          listingToEdit={listingToEdit}
          onClose={handleEditModalClose}
          onRefresh={handleRefreshAfterEdit}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    fontFamily: 'sans-serif',
    margin: '80px auto',
    maxWidth: '1200px',
    padding: '1rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  heading: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  addButton: {
    backgroundColor: 'rgb(44, 105, 160)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.2rem',
    cursor: 'pointer',
    fontSize: '1rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '0.8rem',
    marginBottom: '2rem',
    padding: '1rem 0',
  },
  filterButton: {
    backgroundColor: '#f8f9fa',
    color: '#495057',
    border: '1px solid #dee2e6',
    borderRadius: '30px',
    padding: '0.7rem 1.5rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    minWidth: '120px',
    textAlign: 'center',
  },
  activeButton: {
    backgroundColor: '#006400',
    color: '#fff',
    border: '1px solid #006400',
    borderRadius: '30px',
    padding: '0.7rem 1.5rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    minWidth: '120px',
    textAlign: 'center',
  },
  listingsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
    justifyContent: 'center',
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '2rem',
  },
  loadingSpinner: {
    width: '50px',
    height: '50px',
    border: '5px solid rgba(44, 105, 160, 0.2)',
    borderTop: '5px solid rgb(44, 105, 160)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '1rem',
    color: '#666',
    fontSize: '1.1rem',
  },
  noListings: {
    textAlign: 'center',
    padding: '2rem',
    width: '100%',
  },
};

export default Nehnutelnosti; 